import { JSX } from 'react';
import styled, { css } from 'styled-components';
import StatusCard from './StatusCard';

const noRoundBorder = css`
  border-radius: 0;
`;

const Root = styled.div<{ disableRoundBorder: boolean }>`
  display: flex;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.slateTint(80)};
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.spacing / 2}px;
  ${({ disableRoundBorder }) => disableRoundBorder && noRoundBorder};
`;

export interface SummaryItemBase<T, TypeName extends string> {
  label: string;
  minWidth?: number;
  value: T;
  stick?: boolean;
  withDivider?: boolean;
  testId?: string;
  wideCardShrinkStart?: number;
  __typename: TypeName;
}

export type SummaryDateItem = SummaryItemBase<Date, 'date'>;
export type SummaryHoursItem = SummaryItemBase<number, 'hours'>;
export type SummaryPillStatusItem = SummaryItemBase<string, 'pill-status'>;
export type SummaryPreviewItem = SummaryItemBase<void, 'preview'>;
export type SummaryTextItem = SummaryItemBase<string, 'text'>;
export type SummaryDescriptionItem = SummaryItemBase<DescriptionProps, 'description'>;
export type SummaryComponentItem = Omit<SummaryItemBase<never, 'component'>, 'value'> & {
  valueComponent: React.ReactNode;
};

export type SummaryItem =
  | SummaryDateItem
  | SummaryHoursItem
  | SummaryPillStatusItem
  | SummaryPreviewItem
  | SummaryTextItem
  | SummaryDescriptionItem
  | SummaryComponentItem;

interface DescriptionProps {
  value: string;
  isLoading?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
}

const SummaryItemStat = ({ stick = false, ...item }: SummaryItem): JSX.Element => {
  const stickAt = stick ? 'right' : undefined;

  switch (item.__typename) {
    case 'date':
      return <StatusCard data-testid={item.testId} type="date" {...{ stickAt, ...item }} />;
    case 'hours':
      return <StatusCard data-testid={item.testId} type="hours" {...{ stickAt, ...item }} />;
    case 'pill-status': {
      return <StatusCard data-testid={item.testId} type="pill-status" {...{ stickAt, ...item }} />;
    }
    case 'preview': {
      return <StatusCard data-testid={item.testId} type="preview" {...{ stickAt, ...item }} />;
    }
    case 'text': {
      return <StatusCard data-testid={item.testId} type="text" {...{ stickAt, ...item }} />;
    }
    case 'description': {
      return <StatusCard data-testid={item.testId} type="description" {...{ stickAt, ...item }} />;
    }
    case 'component': {
      const { valueComponent, ...rest } = item;
      return <StatusCard data-testid={item.testId} type="component" valueComponent={valueComponent} {...{ stickAt, ...rest }} />;
    }
    default: {
      return <></>;
    }
  }
};

interface SummaryCardsProps {
  items: SummaryItem[];
  disableRoundBorder?: boolean;
}

const SummaryCards = ({ items, disableRoundBorder = false }: SummaryCardsProps) => {
  return (
    <Root disableRoundBorder={disableRoundBorder}>
      {items.map((item, index) => (
        <SummaryItemStat key={item.label} withDivider={index !== 0} {...item} />
      ))}
    </Root>
  );
};

export default SummaryCards;
